import * as React from 'react';
import { Search } from 'lucide-react';

import { apenasDigitos, cnsValido, cpfValido, mascararCns, mascararCpf } from '@/lib/mascaras';
import type { ChaveDocumento } from '@/features/historico-paciente/tipos';
import { HistoricoPaciente } from '@/components/historico-paciente/historico-paciente';
import { MarcacaoDoHistorico } from '@/components/historico-paciente/marcacao-do-historico';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { Botao } from '@/components/ui/button';
import { Entrada } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Cartao, CartaoConteudo } from '@/components/ui/card';

/** Até 11 dígitos é CPF; daí em diante, CNS. A máscara acompanha a digitação. */
function mascararDocumento(valor: string): string {
  const digitos = apenasDigitos(valor);
  return digitos.length <= 11 ? mascararCpf(digitos) : mascararCns(digitos);
}

function chaveDoDocumento(valor: string): ChaveDocumento | null {
  const digitos = apenasDigitos(valor);
  if (digitos.length === 11 && cpfValido(digitos)) return { cpf: digitos };
  if (digitos.length === 15 && cnsValido(digitos)) return { cns: digitos };
  return null;
}

interface MarcacaoEmImpressao {
  identificador: string;
  chaveConfirmacao: string | null;
}

/**
 * Histórico do paciente no eSUS Regulação e no SISREG 3, a partir do CPF ou
 * do CNS. Aberto a todo perfil; cada consulta fica registrada na auditoria.
 *
 * Das marcações do eSUS agendadas de hoje em diante sai o comprovante, para o
 * paciente que não está no KAVI.
 */
export function PaginaHistoricoPaciente() {
  const [documento, setDocumento] = React.useState('');
  const [erro, setErro] = React.useState<string | undefined>();
  const [chave, setChave] = React.useState<ChaveDocumento | null>(null);
  const [impressao, setImpressao] = React.useState<MarcacaoEmImpressao | null>(null);

  const aoBuscar = (evento: React.FormEvent) => {
    evento.preventDefault();
    const nova = chaveDoDocumento(documento);
    if (!nova) {
      setErro('Informe um CPF (11 dígitos) ou CNS (15 dígitos) válido.');
      return;
    }
    setErro(undefined);
    setChave(nova);
  };

  return (
    <>
      {chave && impressao && (
        <MarcacaoDoHistorico
          documento={chave}
          identificador={impressao.identificador}
          chaveConfirmacao={impressao.chaveConfirmacao}
          aoVoltar={() => setImpressao(null)}
        />
      )}

      {/* Escondido, e não desmontado, durante a impressão: voltar devolve a
          aba e a lista como estavam. */}
      <div hidden={Boolean(impressao)}>
        <div className="flex flex-col gap-4">
          <CabecalhoPagina
            titulo="Histórico do paciente"
            descricao="Solicitações e marcações do paciente no eSUS Regulação e no SISREG 3. A consulta é registrada na auditoria."
          />

          <Cartao>
            <CartaoConteudo className="pt-5">
              <form onSubmit={aoBuscar} noValidate className="flex flex-wrap items-end gap-3">
                <Campo
                  id="historico-documento"
                  rotulo="CPF ou CNS"
                  erro={erro}
                  className="w-full max-w-xs"
                >
                  <Entrada
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="000.000.000-00"
                    value={documento}
                    onChange={(e) => setDocumento(mascararDocumento(e.target.value))}
                  />
                </Campo>
                <Botao type="submit">
                  <Search aria-hidden="true" />
                  Buscar
                </Botao>
              </form>
            </CartaoConteudo>
          </Cartao>

          {/* A `key` recomeça as abas a cada paciente, em vez de herdar a aba do anterior. */}
          {chave && (
            <HistoricoPaciente
              key={JSON.stringify(chave)}
              chave={chave}
              aoImprimirMarcacao={(identificador, chaveConfirmacao) =>
                setImpressao({ identificador, chaveConfirmacao })
              }
            />
          )}
        </div>
      </div>
    </>
  );
}
